import { useCallback, useEffect } from 'react'
import firebase from 'firebase/app'
import { GameBoard, GalacticTraderOffer, PrivateTrade, GameOptions, Sheet, GameMetadata, loadGameArgs, PrometheusTheme, PurchaseAssistOffer, PurchaseAssistRequest } from '@prometheus/types'
import { useSocket } from '../../../hooks/useSocket'
import { usePurchaseAssist } from '../components/PurchaseAssist/PurchaseAssistProvider'
import { purchaseActionLabel } from '../components/PurchaseAssist/purchaseAssist'

const db = firebase.firestore()

export const getUniqueID = () => db.collection('g').doc().id

type upgradeTypes = 'loc_max' | 'loc_rate' | 'bond_rate' | 'bond_max' | 'population_max'
type modifyCashAccountTypes = 'withdraw_loc' | 'withdraw_bonds' | 'deposit_bonds' | 'deposit_loc' | 'balance_cash'
type planetUpgradeActions = 'purchase' | 'loc' | 'all_planet_loans' | 'deposit_all_planet_loans' | 'mine' | 'bulk_mine' | 'barrier' | 'barrier_modal' | 'bulk_barrier' | 'starbase' | 'starbase-refund' | 'tile' | 'downgrade_tile' | 'lock' | 'strength' | 'submit_quest' | 'activate_reward'
type conversionTypes = 'Wa' | 'Au' | 'Oil' | 'population' | 'U' | 'Cu' | 'Ag' | 'Dia' | 'Ti' | 'Fe' | 'Pt'
type privateTradeTypes = 'offer' | 'accept' | 'revoke'

export interface ProfileUpdate {
  name?: string
  theme?: PrometheusTheme
}

let socket: SocketIOClient.Socket

const builder = (endpoint: string) => (data?: any): Promise<any> => {
  return new Promise((res, rej) => {
    const requestId = getUniqueID()
    const handleError = (data: any) => {
      rej(data)
      socket.off(`response-${requestId}`, handleSuccess)
    }
    const handleSuccess = (data: any) => {
      res({ data })
      socket.off(`response-error-${requestId}`, handleError)
    }
    socket.once(`response-${requestId}`, handleSuccess)
    socket.once(`response-error-${requestId}`, handleError)
    socket.emit('invoke', requestId, [endpoint, data])
  })
}

const readyUp = builder('readyUp')
const leaveGame = builder('leaveGame')
const endGame = builder('endGame')
const leaveLobby = builder('leaveLobby')
const deleteGame = builder('deleteGame')
const upgrade = builder('upgrade')
const modifyCashAccounts = builder('modifyCashAccounts')
const getGameBoard = builder('getGameBoard')
export const pickChoosablePlanet = builder('pickChoosablePlanet')
const commitTrade = builder('commitTrade')
const upgradePlanet = builder('upgradePlanet')
const startGame = builder('startGame')
const joinGame = builder('joinGame')
const loadGame = builder('loadGame')
const createGame = builder('createGame')
const convertResource = builder('convertResource')
const attackBoss = builder('attackBoss')
const exportGame = builder('exportGame')
const offerTrade = builder('offerTrade')
const forceTurnEnd = builder('forceTurnEnd')
const setGameOptions = builder('setGameOptions')
const adminSaveGame = builder('adminSaveGame')
const pauseGame = builder('pauseGame')
const addTime = builder('addTime')
const updateProfile = builder('updateProfile')

/**
 * Pull the "want me to cover this?" offer off a refused action. The server
 * attaches it to `details` on the HttpsError, which survives the socket hop.
 */
const extractAssistOffer = (error: any): PurchaseAssistOffer | undefined => error?.details?.assist

export default function usePlayerActions() {
  const sock = useSocket()
  const { requestAssist } = usePurchaseAssist()

  useEffect(() => {
    if (sock) {
      socket = sock
    }
  }, [sock])

  /**
   * Run a purchase; if the server refuses it for want of cash or crew, offer
   * to cover the gap and — once the player accepts — run the SAME action
   * again with the opt-in flags so the server does the withdrawal, the hire
   * and the purchase atomically. Declining resolves quietly, so cancelling
   * the popup doesn't also raise an error toast.
   */
  const withAssist = useCallback(async (
    actionLabel: string | undefined,
    invoke: (assist?: PurchaseAssistRequest) => Promise<any>,
  ) => {
    try {
      return await invoke()
    } catch (e) {
      const offer = extractAssistOffer(e)
      if (!offer) {
        throw e
      }
      const accepted = await requestAssist(offer, actionLabel)
      if (!accepted) {
        return undefined
      }
      return invoke(accepted)
    }
  }, [requestAssist])

  const onReady = useCallback(() => readyUp(), [])
  const onLeaveGame = useCallback(() => leaveGame(), [])
  const onEndGame = useCallback(() => endGame(), [])
  const onLeaveLobby = useCallback(() => leaveLobby(), [])
  const onDeleteGame = useCallback(() => deleteGame(), [])
  const onUpgrade = useCallback((type: upgradeTypes) => withAssist(
    'Upgrade',
    (assist) => (assist ? upgrade([type, assist]) : upgrade(type)),
  ), [withAssist])
  const onModifyCashAccounts = useCallback((type: modifyCashAccountTypes) => modifyCashAccounts(type), [])
  const onGetGameBoard = useCallback((id?: string) => getGameBoard(id), [])
  const onPickChoosablePlanet = useCallback((planet: (keyof GameBoard)) => pickChoosablePlanet(planet), [])
  const onCommitTrade = useCallback((tradeOffer: GalacticTraderOffer) => commitTrade(tradeOffer), [])
  // `preAuthorized` is for controls that already spelled out the trade-off
  // ("Withdraw Cash & Purchase") — they send the flags on the first attempt
  // so the player isn't asked to confirm the same withdrawal twice. Anything
  // still short after that falls back to the assist popup as usual.
  const onUpgradePlanet = useCallback((
    planet: any,
    action: planetUpgradeActions,
    preAuthorized?: PurchaseAssistRequest,
  ) => withAssist(
    purchaseActionLabel(action),
    (assist) => upgradePlanet([planet, action, assist || preAuthorized]),
  ), [withAssist])
  const onStartGame = useCallback(() => startGame(), [])
  const onJoinGame = useCallback((arg) => joinGame(arg), [])
  const onCreateGame = useCallback((arg) => createGame(arg), [])
  const onForceTurnEnd = useCallback(() => forceTurnEnd(), [])
  const onConvertResource = useCallback((type: conversionTypes, quantity: number) => withAssist(
    type === 'population' ? 'Hire' : undefined,
    (assist) => convertResource([type, quantity, assist]),
  ), [withAssist])
  const onAttackBoss = useCallback((missiles: number) => attackBoss({ missiles }), [])
  const onExportGame = useCallback(() => exportGame(), [])
  const onOfferTrade = useCallback((type: privateTradeTypes, offer: PrivateTrade) => offerTrade([type, offer]), [])
  const onSetGameOptions = useCallback((options: Partial<GameOptions>) => setGameOptions(options), [])
  const onAdminSaveGame = useCallback((sheets: Sheet[], metadata: GameMetadata) => adminSaveGame({ sheets, metadata }), [])
  const onPauseGame = useCallback((paused: boolean) => pauseGame(paused), [])
  const onAddTime = useCallback((time: number) => addTime(time), [])
  const onLoadGame = useCallback((args: loadGameArgs) => loadGame(args), [])
  // Account-scoped, not game-scoped: guests have no firebase login, so profile
  // edits go through the server for everyone rather than straight to firestore.
  const onUpdateProfile = useCallback((update: ProfileUpdate) => updateProfile(update), [])

  return {
    onReady,
    onLeaveGame,
    onEndGame,
    onLeaveLobby,
    onDeleteGame,
    onUpgrade,
    onModifyCashAccounts,
    onGetGameBoard,
    onPickChoosablePlanet,
    onCommitTrade,
    onUpgradePlanet,
    onStartGame,
    onForceTurnEnd,
    onJoinGame,
    onCreateGame,
    onConvertResource,
    onAttackBoss,
    onExportGame,
    onOfferTrade,
    onSetGameOptions,
    onAdminSaveGame,
    onPauseGame,
    onLoadGame,
    onAddTime,
    onUpdateProfile,
  }
}
